import { isApiFailureBody, isApiSuccessBody } from "@/lib/api/api-envelope"
import type { PersistedRsvpStatus } from "@/lib/events/types"

/** Wire status for the signed-in viewer (`mine` null → `no_response`). */
export type RsvpWireUserStatus = PersistedRsvpStatus | "no_response"

export type EventRsvpSnapshot = {
  mine: PersistedRsvpStatus | null
  userStatus: RsvpWireUserStatus
  counts: { going: number; maybe: number; declined: number }
}

function snapshotUserStatus(mine: PersistedRsvpStatus | null): RsvpWireUserStatus {
  return mine ?? "no_response"
}

async function parseEnvelope<T>(
  res: Response,
  label: string,
): Promise<T> {
  const body: unknown = await res.json()
  if (!res.ok || isApiFailureBody(body)) {
    const msg = isApiFailureBody(body)
      ? body.error.message
      : `${label} failed: ${res.status}`
    throw new Error(msg)
  }
  if (!isApiSuccessBody<T>(body)) {
    throw new Error(`${label} failed: malformed response`)
  }
  return body.data
}

function withUserStatus(snap: EventRsvpSnapshot): EventRsvpSnapshot {
  return {
    ...snap,
    userStatus: snap.userStatus ?? snapshotUserStatus(snap.mine),
  }
}

type FetchOpts = { signal?: AbortSignal }

export async function postEventRsvp(
  eventId: string,
  status: PersistedRsvpStatus | "no_response",
  occurrenceId?: string | null,
  opts?: FetchOpts,
): Promise<EventRsvpSnapshot> {
  const id = eventId.trim()
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/rsvp`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      status,
      ...(occurrenceId?.trim() ? { occurrenceId: occurrenceId.trim() } : {}),
    }),
    signal: opts?.signal,
  })
  return withUserStatus(await parseEnvelope<EventRsvpSnapshot>(res, "POST RSVP"))
}

export async function deleteEventRsvp(
  eventId: string,
  occurrenceId?: string | null,
  opts?: FetchOpts,
): Promise<EventRsvpSnapshot> {
  const id = eventId.trim()
  const q = occurrenceId?.trim()
    ? `?occurrenceId=${encodeURIComponent(occurrenceId.trim())}`
    : ""
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/rsvp${q}`, {
    method: "DELETE",
    signal: opts?.signal,
  })
  return withUserStatus(await parseEnvelope<EventRsvpSnapshot>(res, "DELETE RSVP"))
}

export async function fetchEventRsvpSnapshot(
  eventId: string,
  occurrenceId?: string | null,
  opts?: FetchOpts,
): Promise<EventRsvpSnapshot> {
  const id = eventId.trim()
  const q = occurrenceId?.trim()
    ? `?occurrenceId=${encodeURIComponent(occurrenceId.trim())}`
    : ""
  const res = await fetch(`/api/events/${encodeURIComponent(id)}/rsvp${q}`, {
    cache: "no-store",
    signal: opts?.signal,
  })
  return withUserStatus(await parseEnvelope<EventRsvpSnapshot>(res, "GET RSVP"))
}
